/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Hardware Capability Detection
   Probes browser environment for WebGL2, WebGPU, WASM, OffscreenCanvas.
   ═══════════════════════════════════════════════════════════════ */

export interface RendererCapabilities {
  readonly webgl2: boolean;
  readonly webgpu: boolean;
  readonly offscreenCanvas: boolean;
  readonly wasm: boolean;
  readonly sharedArrayBuffer: boolean;
  readonly maxTextureSize: number;
  readonly maxTextureUnits: number;
  readonly floatTextures: boolean;
  readonly halfFloatTextures: boolean;
}

let cachedCapabilities: RendererCapabilities | null = null;

export function detectRendererCapabilities(): RendererCapabilities {
  if (cachedCapabilities) return cachedCapabilities;

  let webgl2 = false;
  let maxTextureSize = 2048;
  let maxTextureUnits = 8;
  let floatTextures = false;
  let halfFloatTextures = false;

  if (typeof document !== 'undefined') {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2');
      if (gl) {
        webgl2 = true;
        maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 4096;
        maxTextureUnits = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS) || 16;
        floatTextures = !!gl.getExtension('EXT_color_buffer_float');
        halfFloatTextures = !!gl.getExtension('EXT_color_buffer_half_float') || floatTextures;
        // Clean up context extensions
        const loseContext = gl.getExtension('WEBGL_lose_context');
        loseContext?.loseContext();
      }
    } catch {
      webgl2 = false;
    }
  }

  const webgpu = typeof navigator !== 'undefined' && 'gpu' in navigator;
  const offscreenCanvas = typeof OffscreenCanvas !== 'undefined';
  const wasm = typeof WebAssembly !== 'undefined';
  const sharedArrayBuffer = typeof SharedArrayBuffer !== 'undefined';

  cachedCapabilities = {
    webgl2,
    webgpu,
    offscreenCanvas,
    wasm,
    sharedArrayBuffer,
    maxTextureSize,
    maxTextureUnits,
    floatTextures,
    halfFloatTextures,
  };

  return cachedCapabilities;
}
